import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Button, FormControl } from '@mui/material'
import { isEqual, omit, uniqBy } from 'lodash'
import { H3 } from 'ui/shorthands/Texts'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import { CloseDateSettingProps } from 'types/med'
import DatePicker from 'ui/partials/DatePicker'
import { isValid } from 'date-fns'
import CloseIconButton from 'ui/shorthands/CloseIconButton'
import {
  formatDateString,
  formatYearDateJpDayOfWeek,
  parseDateString,
} from 'ui/utils/dateFormatUtils'
import { startOfJstToDay } from 'ui/utils/jstDatetime'

type EditableCloseDateSettingProps = {
  id: number // NOTE: map内の"key"に指定するためのid
} & Partial<CloseDateSettingProps>

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
    color: theme.palette.text.primary,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.25),
  },
  listItem: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: 'auto 328px 1fr',
    columnGap: theme.spacing(2),
    padding: `${theme.spacing(1)} 0`,
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  readOnlyListItem: {
    display: 'block',
    fontSize: theme.typography.body2.fontSize,
    padding: theme.spacing(2),
    lineHeight: '16px',
    letterSpacing: '0.5px',
  },
  empty: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    fontSize: theme.typography.body1.fontSize,
    lineHeight: '24px',
    letterSpacing: '0.5px',
  },
}))

const HeaderControl: React.FC<{
  onClickCreateButton: () => void
  readOnly?: boolean
}> = ({ onClickCreateButton, readOnly }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  return (
    <div className={classes.header}>
      <H3>
        <QuestionTooltipWithLabel label={t('予約不可日')}>
          {t(
            '定期予定、個別予定可能日よりも優先して設定されます。日付を過ぎると表示されなくなります。'
          )}
        </QuestionTooltipWithLabel>
      </H3>
      {!readOnly && (
        <FormControl>
          <Button
            color="primary"
            variant="contained"
            onClick={onClickCreateButton}
          >
            {t('新規作成')}
          </Button>
        </FormControl>
      )}
    </div>
  )
}

const CloseDateSettingListItem: React.FC<{
  closeDateSetting: EditableCloseDateSettingProps
  onChange: (closeDateSetting: EditableCloseDateSettingProps) => void
  onDelete?: () => void
  readOnly?: boolean
}> = ({ closeDateSetting, onChange, onDelete, readOnly }) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const [closeDate, setCloseDate] = useState<Date | null>(
    closeDateSetting.close_date
      ? parseDateString(closeDateSetting.close_date)
      : null
  )

  useEffect(() => {
    const valid =
      closeDate &&
      isValid(closeDate) &&
      closeDate.valueOf() >= startOfJstToDay.valueOf() // NOTE: 今日以降の日付であること
    const close_date = valid ? formatDateString(closeDate!) : null

    const newCloseDateSetting: EditableCloseDateSettingProps = {
      id: closeDateSetting.id,
    }
    if (close_date) {
      newCloseDateSetting.close_date = close_date
    }
    // 差分がある場合のみonChangeを呼ぶ
    if (!isEqual(newCloseDateSetting, closeDateSetting)) {
      onChange(newCloseDateSetting)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDate])

  return (
    <div className={cx(classes.listItem, readOnly && classes.readOnlyListItem)}>
      {readOnly ? (
        formatYearDateJpDayOfWeek(closeDate!)
      ) : (
        <>
          <CloseIconButton onClick={onDelete} />
          <DatePicker
            label={t('日付')}
            onDateChange={setCloseDate}
            date={closeDate}
            invalidDateMessage={t('日付の形式が不正です')}
            minDate={startOfJstToDay}
            minDateMessage={t('過去の日付は指定できません')}
          />
        </>
      )}
    </div>
  )
}

// NOTE: ユニークidを得るためのカウンタ
let editableCloseDateSettingId = 1

// NOTE: "key" に利用するためのidを付加したオブジェクトを返す
const createNewEditableCloseDateSetting = (obj?: CloseDateSettingProps) => ({
  ...obj,
  id: editableCloseDateSettingId++,
})

type CloseDateSettingListProps = {
  /** 予約不可日一覧 */
  closeDateSettings: CloseDateSettingProps[]

  /** 内容が更新された場合に発火 */
  onChange?: (closeDateSettings: CloseDateSettingProps[]) => void

  /** 参照のみのモードを指定 */
  readOnly?: boolean
}
const CloseDateSettingList: React.FC<CloseDateSettingListProps> = ({
  closeDateSettings,
  onChange,
  readOnly,
}: CloseDateSettingListProps) => {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<EditableCloseDateSettingProps[]>(
    closeDateSettings.map(createNewEditableCloseDateSetting)
  )

  const onChangeRef = useRef(onChange)

  // NOTE: propsの更新を内部ステートに反映
  useEffect(() => {
    setSettings(closeDateSettings.map(createNewEditableCloseDateSetting))
  }, [closeDateSettings])

  useEffect(() => {
    const validSettings = settings
      .filter(({ close_date }) => close_date)
      .map((setting) => {
        // idを抜いてCloseDateSettingPropsにする
        return omit(setting, 'id') as CloseDateSettingProps
      })

    if (!isEqual(closeDateSettings, validSettings)) {
      onChangeRef.current?.(validSettings)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings])

  const { classes } = useStyles()

  return (
    <div className={classes.root}>
      <HeaderControl
        onClickCreateButton={() => {
          setSettings((prevState) => [
            createNewEditableCloseDateSetting(),
            ...prevState,
          ])
        }}
        readOnly={readOnly}
      />
      {settings.length > 0 ? (
        settings.map((setting, index) => (
          <CloseDateSettingListItem
            key={setting.id}
            closeDateSetting={setting}
            onChange={(newSetting) => {
              setSettings((prevState) => {
                const newSettings = prevState.map(
                  (existingSetting, targetIndex) =>
                    targetIndex === index ? newSetting : existingSetting
                )

                // 重複があれば削除
                const newSettingsWithoutDuplicate = uniqBy(
                  newSettings,
                  (setting) => setting.close_date
                )

                return newSettingsWithoutDuplicate
              })
            }}
            onDelete={() => {
              setSettings((prevState) =>
                prevState.filter((_, targetIndex) => targetIndex !== index)
              )
            }}
            readOnly={readOnly}
          />
        ))
      ) : (
        <div className={classes.empty}>{t('設定がありません')}</div>
      )}
    </div>
  )
}

export default CloseDateSettingList
